import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type BlogPostSummary = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string | null;
  tag: string;
  author_name: string;
  published_at: string | null;
  reading_minutes: number;
};

export type BlogPostFull = BlogPostSummary & {
  body_markdown: string;
  meta_description: string;
  updated_at: string | null;
  word_count: number;
};

function wordCount(markdown: string) {
  return markdown.trim().split(/\s+/).filter(Boolean).length;
}
function readingMinutes(markdown: string) {
  return Math.max(1, Math.round(wordCount(markdown) / 200));
}

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

// ===== Public reads =====

export const listPublishedPosts = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("blog_posts")
    .select("id, slug, title, excerpt, cover_image_url, tag, author_name, published_at, body_markdown")
    .eq("published", true)
    .order("published_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  const rows: BlogPostSummary[] = (data ?? []).map((r) => ({
    id: r.id,
    slug: r.slug,
    title: r.title,
    excerpt: r.excerpt,
    cover_image_url: r.cover_image_url,
    tag: r.tag,
    author_name: r.author_name,
    published_at: r.published_at,
    reading_minutes: readingMinutes(r.body_markdown ?? ""),
  }));
  return { rows };
});

export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => ({ slug: String(d?.slug ?? "").trim() }))
  .handler(async ({ data }) => {
    if (!data.slug) return { post: null };
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("blog_posts")
      .select("id, slug, title, excerpt, cover_image_url, tag, author_name, published_at, updated_at, body_markdown, meta_description")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return { post: null };
    const body = row.body_markdown ?? "";
    const post: BlogPostFull = {
      id: row.id,
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt,
      cover_image_url: row.cover_image_url,
      tag: row.tag,
      author_name: row.author_name,
      published_at: row.published_at,
      updated_at: row.updated_at ?? null,
      reading_minutes: readingMinutes(body),
      word_count: wordCount(body),
      body_markdown: body,
      meta_description: row.meta_description || row.excerpt,
    };
    return { post };
  });

export const listPostSlugsForSitemap = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("blog_posts")
    .select("slug, published_at, updated_at")
    .eq("published", true);
  if (error) throw new Error(error.message);
  return { rows: data ?? [] };
});

// ===== Admin =====

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error("Role check failed");
  if (!data) throw new Error("Forbidden");
}

export const listAllPostsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select("id, slug, title, tag, published, published_at, updated_at, created_at")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { rows: data ?? [] };
  });

export const getPostByIdAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => ({ id: String(d?.id ?? "").trim() }))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    if (!data.id) return { post: null };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { post: row };
  });

type BlogPostInput = {
  id?: string | null;
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string;
  tag: string;
  body_markdown: string;
  meta_description: string;
  author_name: string;
  published: boolean;
};

export const upsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: BlogPostInput) => {
    const slug = String(d?.slug ?? "").trim().toLowerCase();
    const title = String(d?.title ?? "").trim();
    if (!slug) throw new Error("Slug fehlt");
    if (!/^[a-z0-9-]+$/.test(slug)) throw new Error("Slug nur a–z, 0–9, Bindestrich");
    if (!title) throw new Error("Titel fehlt");
    return {
      id: d.id ?? null,
      slug,
      title,
      excerpt: String(d?.excerpt ?? "").trim(),
      cover_image_url: String(d?.cover_image_url ?? "").trim() || null,
      tag: String(d?.tag ?? "Allgemein").trim() || "Allgemein",
      body_markdown: String(d?.body_markdown ?? ""),
      meta_description: String(d?.meta_description ?? "").trim().slice(0, 200),
      author_name: String(d?.author_name ?? "PIXIWEB").trim() || "PIXIWEB",
      published: !!d?.published,
    };
  })
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const payload = {
      slug: data.slug,
      title: data.title,
      excerpt: data.excerpt,
      cover_image_url: data.cover_image_url,
      tag: data.tag,
      body_markdown: data.body_markdown,
      meta_description: data.meta_description,
      author_name: data.author_name,
      published: data.published,
      published_at: data.published ? new Date().toISOString() : null,
    };
    if (data.id) {
      // Don't overwrite published_at if already published
      const { data: existing } = await supabaseAdmin
        .from("blog_posts")
        .select("published, published_at")
        .eq("id", data.id)
        .maybeSingle();
      const finalPayload = { ...payload };
      if (data.published && existing?.published && existing.published_at) {
        finalPayload.published_at = existing.published_at;
      }
      const { error } = await supabaseAdmin.from("blog_posts").update(finalPayload).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const { data: inserted, error } = await supabaseAdmin
      .from("blog_posts")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted?.id };
  });

export const deletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => {
    const id = String(d?.id ?? "").trim();
    if (!id) throw new Error("ID fehlt");
    return { id };
  })
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
